import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import butterflyApi from '../ButterflyApi.js';
import BookColumnList from '../Main/BookColumnList.js';
import MobilePage from '../UiComponents/MobilePage';
import { isLogin } from '../Utils/Cookies.js';
import Utils from '../utils';
import './FairyTail.css';

const FairyTail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scripts, setScripts] = useState([]);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [heroNo, setHeroNo] = useState(1);
  const [heroes, setHeroes] = useState([]);
  const [loading, setLoading] = useState(true);
  const beforeScriptNo = searchParams.get('beforeScriptNo');
  const sample = searchParams.get('sample') === 'true';
  const selectedHero = useMemo(() => {
    return heroes.find(hero => String(hero.heroNo) === String(heroNo));
  }, [heroes, heroNo]);
  const sampleScripts = useMemo(() => {
    return scripts.filter(script => Utils.isSampleScript(script));
  }, [scripts]);
  const filteredScripts = useMemo(() => {
    if (sample) return scripts;

    return scripts.filter(script => !Utils.isSampleScript(script));
  }, [scripts, sample]);
  const storyCreationLimit = selectedHero?.storyCreationLimit;
  const pendingStoryCount = scriptsLoaded && !loading && !sample && Number.isInteger(storyCreationLimit)
    ? Math.max(0, storyCreationLimit - filteredScripts.length)
    : 0;
  const totalStoryCount = filteredScripts.length + pendingStoryCount;
  const isSampleOnly = !sample && scripts.length > 0 && filteredScripts.length === 0;
  const hasSampleScripts = sampleScripts.length > 0;

  const loadScripts = async () => {
    let selectedHeroNo = isLogin() ? localStorage.getItem('heroNo') : 1;

    setLoading(true);
    setScriptsLoaded(false);
    try {
      if (isLogin()) {
        const heroesResponse = await butterflyApi.getHeroes();
        const loadedHeroes = heroesResponse?.items || [];
        setHeroes(loadedHeroes);
        localStorage.setItem('heroes', JSON.stringify(loadedHeroes));

        if (loadedHeroes.length > 0) {
          const hasSelectedHero = loadedHeroes.some(hero => String(hero.heroNo) === String(selectedHeroNo));
          selectedHeroNo = hasSelectedHero ? selectedHeroNo : loadedHeroes[0].heroNo;
          localStorage.setItem('heroNo', selectedHeroNo);
        } else {
          selectedHeroNo = null;
          localStorage.removeItem('heroNo');
        }
      } else {
        setHeroes([]);
      }

      setHeroNo(selectedHeroNo || 1);
      const response = await butterflyApi.getScripts(isLogin() && selectedHeroNo);
      setScripts(response?.items || []);
      setScriptsLoaded(Array.isArray(response?.items));
    } finally {
      setLoading(false);
    }
  };

  const renderSampleOnlyMessage = () => {
    if (!selectedHero) {
      return <>
        <h3>첫 번째 이야기를 기다리고 있어요</h3>
        <p>주인공을 추가하면 우리 아이의 모습과 이름이<br />담긴 특별한 동화를 만들 수 있어요.</p>
        <Link
          className='book-primary-button'
          to={isLogin() ? '/heroes/add' : '/login?redirect=/heroes/add'}
        >
          주인공 추가하기 <span aria-hidden='true'>＋</span>
        </Link>
      </>;
    }

    if (!selectedHero.writingReady) {
      return <>
        <h3>이야기를 시작할 준비를 해볼까요?</h3>
        <p>아이와 가족을 소개하고 동화 제작을 신청해 주세요.<br />익숙한 얼굴들이 이야기 속 주인공이 돼요.</p>
        <Link
          className='book-primary-button'
          to={`/heroes/${selectedHero.heroNo}/setting`}
        >
          주인공 설정하기 <span aria-hidden='true'>→</span>
        </Link>
      </>;
    }

    return <>
      <h3>우리 아이의 첫 동화를 만들어볼까요?</h3>
      <p>동화 제작을 신청하면 우리 가족만의<br />특별한 이야기를 이 책장에서 만날 수 있어요.</p>
      <Link className='book-primary-button' to='/apps?category=fairytale'>동화 제작 신청하기 <span aria-hidden='true'>→</span></Link>
    </>;
  };

  const onBookClick = (scriptNo, isSampleStory) => {
    const code = Utils.makeCode(scriptNo, heroNo);
    const detailParams = new URLSearchParams();

    if (isSampleStory) {
      detailParams.set('sample', 'true');
    }

    navigate(`/fairytales/${code}${detailParams.toString() ? `?${detailParams.toString()}` : ''}`);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    loadScripts();
  }, []);

  return <MobilePage className='storybook-page fairytales-page' loading={loading}>
    <main className='fairytales-content'>
      <header className='fairytales-intro'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 한 편씩, 상상이 자라는 시간</p>
        <div className='fairytales-title-row'>
          <h1 className='book-title'>이야기 책장</h1>
          <img src='/image/main/books.png' alt='' width='64' height='64' />
        </div>
        <p className='book-description'>오늘은 어떤 모험을 떠나볼까요?<br />아이와 함께 마음에 드는 이야기를 펼쳐보세요.</p>
      </header>

      {(hasSampleScripts || sample) && <nav className='fairytales-collection-nav' aria-label='동화 모아보기'>
        <Link to='/fairytales' aria-current={!sample ? 'page' : undefined}>내 동화</Link>
        <Link to='/fairytales?sample=true' aria-current={sample ? 'page' : undefined}>샘플 함께 보기</Link>
      </nav>}

      <section className='fairytales-library' aria-labelledby='fairytales-heading' aria-busy={loading}>
        <div className='fairytales-section-heading'>
          <div>
            <h2 id='fairytales-heading'>{sample ? '모든 이야기' : '우리 아이의 동화'}</h2>
            {pendingStoryCount > 0
              ? <p role='status'>읽을 수 있는 동화 {filteredScripts.length}편 · 생성 중 {pendingStoryCount}편</p>
              : <p>{sample ? '샘플 동화도 함께 만나보세요.' : '우리 가족만의 특별한 이야기를 모았어요.'}</p>}
          </div>
          {!loading && <span className='fairytales-count' aria-label={`동화 ${totalStoryCount}편`}>{totalStoryCount}편</span>}
        </div>
        <BookColumnList scripts={filteredScripts} pendingStoryCount={pendingStoryCount} onBookClick={onBookClick} initialVisibleScriptNo={beforeScriptNo} />
        {!loading && totalStoryCount === 0 && <div className='fairytales-empty'>
          <img className='fairytales-empty-art' src='/image/fairytales/story-library.webp' alt='' width='1681' height='936' />
          <div className='fairytales-empty-copy'>
            {isSampleOnly ? renderSampleOnlyMessage() : <>
              <h3>아직 책장에 동화가 없어요</h3>
              <p>새로운 이야기가 준비되면 여기에서 만나보세요.</p>
            </>}
            {hasSampleScripts && !sample && <Link className='book-text-link' to='/fairytales?sample=true'>샘플 동화 먼저 둘러보기 <span aria-hidden='true'>↗</span></Link>}
          </div>
        </div>}
      </section>

      {!loading && !sample && <aside className='fairytales-create' aria-labelledby='fairytales-create-heading'>
        <span className='book-eyebrow'><span aria-hidden='true'>✧</span> 다음 이야기의 주인공</span>
        <h2 id='fairytales-create-heading'>우리 아이만의 동화를 만들어보세요</h2>
        <p>아이의 이름과 모습, 사랑하는 가족을 담아<br />오래 간직할 이야기를 선물해 주세요.</p>
        <Link className='book-primary-button' to='/apps?category=fairytale'>추가 동화 제작 신청하기 <span aria-hidden='true'>→</span></Link>
      </aside>}
      {!loading && sample && <Link className='book-secondary-button fairytales-return' to='/fairytales'>내 동화 보기 <span aria-hidden='true'>→</span></Link>}
      <p className='fairytales-footnote'><span aria-hidden='true'>✦</span> 함께 읽는 오늘이, 아이에게는 오래 남을 이야기.</p>
    </main>
  </MobilePage>
}

export default FairyTail
